import { useId, useState } from 'react';
import { faPaperPlane, faPlus } from '@fortawesome/free-solid-svg-icons';
import { CustomerButton } from '@/components/customer-portal/CustomerButton';
import {
  CustomerWorkOrderApprovalForm,
  type CustomerWorkOrderApprovalPayload,
} from '@/features/customer-portal/jobs/CustomerWorkOrderApprovalForm';
import type {
  CustomerThreadCapabilities,
  CustomerThreadQuotation,
} from '@/types/customer-request-thread.types';

/**
 * Stage D-v — what the customer may DO on their request (D-145).
 *
 * ── THE SERVER DECIDES; THIS FILE ONLY DRAWS ────────────────────────────────
 * Every control below is gated on ONE thing: the matching flag in
 * `capabilities`, which the server derived from the caller's own permission AND
 * the round's real state by probing the state machine itself.
 *
 * There is deliberately no `status === 'QUOTATION_READY'`, no
 * `awaitingYourResponse`, no `quotation !== null`, no role check and no round
 * number anywhere in this component's authorization path. Re-deriving any of
 * that here would be a second, weaker copy of the state machine — one that
 * could drift from the real one and would be trivially editable in a browser.
 * The client conditions that DO exist decide only presentation: which panel is
 * open, whether a confirmation step is showing.
 *
 * ── NOTHING IS INVENTED AFTER A WRITE ───────────────────────────────────────
 * No action appends a timeline event, edits the status, or flips a capability
 * locally. Each one calls the existing customer endpoint and then asks the
 * server for the thread again. The entries and the new state are the server's
 * to create, and the next render shows exactly what it created.
 */

export interface CustomerThreadActionHandlers {
  onReply: (message: string) => Promise<void>;
  onAdd: (message: string) => Promise<void>;
  onApprove: (payload: CustomerWorkOrderApprovalPayload) => Promise<void>;
  onReject: (message: string) => Promise<void>;
  onRequestModification: (requestText: string) => Promise<void>;
}

interface Props {
  capabilities: CustomerThreadCapabilities;
  /** Shown in the approval confirmation so the customer can see WHICH quotation
   * they are accepting. Presentation only — it is never the authority. */
  quotation: CustomerThreadQuotation | null;
  /** True while any mutation is in flight, from the page that owns it. */
  submitting: boolean;
  handlers: CustomerThreadActionHandlers;
}

/** The server's own bound: `ModificationMessageDto` / `requestText`, 1 … 4000. */
const MAX_MESSAGE = 4000;

const TEXTAREA_CLASS =
  'mt-1.5 block min-h-[5rem] w-full resize-y rounded-customer-sm border border-customer-border bg-customer-surface px-3.5 py-2.5 text-sm leading-5 text-customer-text placeholder:text-customer-text-secondary transition-colors hover:border-customer-text/30 focus:border-customer-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring/40 disabled:cursor-not-allowed disabled:opacity-60';

/** A labelled message box with a submit. Used by Reply, Add, Reject and
 * Request-further-modification, so all four validate and disable identically. */
function MessageForm({
  label,
  placeholder,
  submitLabel,
  testId,
  submitting,
  required,
  onSubmit,
  onCancel,
}: {
  label: string;
  placeholder: string;
  submitLabel: string;
  testId: string;
  submitting: boolean;
  /** Reject may be sent without a reason; the other three may not. */
  required: boolean;
  onSubmit: (text: string) => void;
  onCancel?: () => void;
}) {
  const uid = useId();
  const [text, setText] = useState('');
  const value = text.trim();
  // Mirrors the server's contract so the customer is told before the round
  // trip — it does not replace it. The server validates again either way.
  const complete = required ? value.length > 0 : true;

  return (
    <form
      className="mt-3"
      aria-label={label}
      onSubmit={(e) => {
        e.preventDefault();
        // The submitting guard is what makes a double-click one request.
        if (submitting || !complete) return;
        onSubmit(value);
        setText('');
      }}
    >
      <label htmlFor={uid} className="block text-sm font-medium text-customer-text">
        {label}
      </label>
      <textarea
        id={uid}
        data-testid={`${testId}-input`}
        className={TEXTAREA_CLASS}
        placeholder={placeholder}
        maxLength={MAX_MESSAGE}
        value={text}
        disabled={submitting}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="mt-2.5 flex flex-wrap gap-2">
        <CustomerButton
          type="submit"
          icon={faPaperPlane}
          disabled={submitting || !complete}
          data-testid={`${testId}-submit`}
        >
          {submitting ? 'Sending…' : submitLabel}
        </CustomerButton>
        {onCancel && (
          <CustomerButton type="button" variant="outline" disabled={submitting} onClick={onCancel}>
            Cancel
          </CustomerButton>
        )}
      </div>
    </form>
  );
}

type DecisionPanel = 'approve' | 'reject' | 'modify' | null;

export function CustomerThreadActions({ capabilities, quotation, submitting, handlers }: Props) {
  const [panel, setPanel] = useState<DecisionPanel>(null);
  const [confirmingApproval, setConfirmingApproval] = useState(false);
  const [pendingApproval, setPendingApproval] = useState<CustomerWorkOrderApprovalPayload | null>(
    null,
  );
  const [confirmingReject, setConfirmingReject] = useState<string | null>(null);

  const { canReply, canAdd, canApprove, canReject, canRequestModification } = capabilities;
  const canDecide = canApprove || canReject || canRequestModification;

  // Every flag false — a Read-Only contact, or a round that owes nothing. The
  // thread still reads; there is simply nothing to do on it.
  if (!canReply && !canAdd && !canDecide) return null;

  const close = () => {
    setPanel(null);
    setConfirmingApproval(false);
    setPendingApproval(null);
    setConfirmingReject(null);
  };

  return (
    <div className="space-y-3" data-testid="customer-thread-actions">
      {canReply && (
        <MessageForm
          label="Reply to the service team"
          placeholder="Answer the question here…"
          submitLabel="Send reply"
          testId="customer-thread-reply"
          submitting={submitting}
          required
          onSubmit={(text) => void handlers.onReply(text)}
        />
      )}

      {canAdd && (
        <MessageForm
          label="Add to your request"
          placeholder="Anything else the service team should know…"
          submitLabel="Add to request"
          testId="customer-thread-add"
          submitting={submitting}
          required
          onSubmit={(text) => void handlers.onAdd(text)}
        />
      )}

      {canDecide && (
        <div className="rounded-customer border border-customer-border p-3.5">
          <p className="text-sm font-medium text-customer-text">Your decision</p>
          <p className="mt-0.5 text-xs text-customer-text-secondary">
            Choose one option. You will confirm before anything is sent.
          </p>

          <div className="mt-3 flex flex-wrap gap-2">
            {canApprove && (
              <CustomerButton
                variant={panel === 'approve' ? 'primary' : 'outline'}
                disabled={submitting}
                data-testid="customer-thread-approve"
                onClick={() => {
                  close();
                  setPanel('approve');
                }}
              >
                Approve
              </CustomerButton>
            )}
            {canReject && (
              <CustomerButton
                variant={panel === 'reject' ? 'primary' : 'outline'}
                disabled={submitting}
                data-testid="customer-thread-reject"
                onClick={() => {
                  close();
                  setPanel('reject');
                }}
              >
                Reject
              </CustomerButton>
            )}
            {canRequestModification && (
              <CustomerButton
                variant={panel === 'modify' ? 'primary' : 'outline'}
                icon={faPlus}
                disabled={submitting}
                data-testid="customer-thread-modify"
                onClick={() => {
                  close();
                  setPanel('modify');
                }}
              >
                Request another change
              </CustomerButton>
            )}
          </div>

          {/* ── Approve ───────────────────────────────────────────────────────
              Two deliberate steps. The Work Order form collects and validates
              the number, description and PDF the server requires (D-129 rule 2),
              and only then does a confirmation name the exact quotation being
              accepted — so approval can never be a single accidental click, and
              never reads as accepting "some" quotation. */}
          {panel === 'approve' && !confirmingApproval && (
            <CustomerWorkOrderApprovalForm
              submitting={submitting}
              onCancel={close}
              onSubmit={(payload) => {
                setPendingApproval(payload);
                setConfirmingApproval(true);
              }}
            />
          )}

          {panel === 'approve' && confirmingApproval && pendingApproval && (
            <div
              className="customer-slide-up mt-4 rounded-customer border border-customer-primary/25 bg-customer-primary/[0.04] p-4"
              data-testid="customer-thread-approve-confirm"
            >
              <p className="text-sm font-medium text-customer-text">Confirm your approval</p>
              <p className="mt-1 text-sm leading-6 text-customer-text-secondary">
                You are approving{' '}
                <span className="font-medium text-customer-text">
                  {quotation
                    ? `Revision ${quotation.revisionNumber}${
                        quotation.quotationReference ? ` (${quotation.quotationReference})` : ''
                      }`
                    : 'the current quotation'}
                </span>
                , the quotation shown on this request. Your Work Order number, description and PDF
                will be sent to the service team.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <CustomerButton
                  disabled={submitting}
                  data-testid="customer-thread-approve-confirm-submit"
                  onClick={() => void handlers.onApprove(pendingApproval)}
                >
                  {submitting ? 'Sending…' : 'Confirm approval'}
                </CustomerButton>
                <CustomerButton
                  variant="outline"
                  disabled={submitting}
                  onClick={() => setConfirmingApproval(false)}
                >
                  Back
                </CustomerButton>
              </div>
            </div>
          )}

          {/* ── Reject ────────────────────────────────────────────────────────
              Also two steps. A rejection ends the round, so it must not be
              reachable by one stray click. */}
          {panel === 'reject' && confirmingReject === null && (
            <MessageForm
              label="Why are you rejecting this quotation? (optional)"
              placeholder="Let the service team know why, if you would like to…"
              submitLabel="Continue"
              testId="customer-thread-reject-reason"
              submitting={submitting}
              required={false}
              onSubmit={(text) => setConfirmingReject(text)}
              onCancel={close}
            />
          )}

          {panel === 'reject' && confirmingReject !== null && (
            <div
              className="customer-slide-up mt-4 rounded-customer border border-customer-border p-4"
              data-testid="customer-thread-reject-confirm"
            >
              <p className="text-sm font-medium text-customer-text">Confirm your rejection</p>
              <p className="mt-1 text-sm leading-6 text-customer-text-secondary">
                You are rejecting{' '}
                <span className="font-medium text-customer-text">
                  {quotation
                    ? `Revision ${quotation.revisionNumber}${
                        quotation.quotationReference ? ` (${quotation.quotationReference})` : ''
                      }`
                    : 'the current quotation'}
                </span>
                . The service team will be told.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <CustomerButton
                  disabled={submitting}
                  data-testid="customer-thread-reject-confirm-submit"
                  onClick={() => void handlers.onReject(confirmingReject)}
                >
                  {submitting ? 'Sending…' : 'Confirm rejection'}
                </CustomerButton>
                <CustomerButton
                  variant="outline"
                  disabled={submitting}
                  onClick={() => setConfirmingReject(null)}
                >
                  Back
                </CustomerButton>
              </div>
            </div>
          )}

          {/* ── Request another change ────────────────────────────────────────
              The server requires the successor round's own request text
              (`requestText`, 1 … 4000), so it is validated before submit. */}
          {panel === 'modify' && (
            <MessageForm
              label="What would you like changed?"
              placeholder="Describe the change you need…"
              submitLabel="Send request"
              testId="customer-thread-modify-reason"
              submitting={submitting}
              required
              onSubmit={(text) => void handlers.onRequestModification(text)}
              onCancel={close}
            />
          )}
        </div>
      )}
    </div>
  );
}

/** Re-exported so the page needs one import for the action vocabulary. */
export type { CustomerWorkOrderApprovalPayload };
